import { Guid } from '../utils/guid';
import { ProjectRole } from './user.model';

export type TeamMemberStatus = 'invited' | 'active' | 'declined' | 'removed';
export type TeamMemberRatePeriod = 'hour' | 'day' | 'week';

export class TeamMember {
  reference: string;
  teamReference: string;
  userReference?: string;
  email: string;
  displayName: string;
  role?: string;
  projectRole?: ProjectRole;
  allocatedChapterReferences?: string[];
  status: TeamMemberStatus;

  weeklyHours: number;
  rateAmount: number;
  ratePeriod: TeamMemberRatePeriod;
  weeklyCost: number;

  invitedAt?: string;
  joinedAt?: string;
  invitationUrl?: string;
  deliveryStatus?: 'not_sent' | 'sent';

  constructor(teamReference: string, email: string, displayName = '', role = 'Developer', projectRole: ProjectRole = 'developer') {
    this.reference = Guid.newGuid();
    this.teamReference = teamReference;
    this.email = email;
    this.displayName = displayName || email;
    this.role = role;
    this.projectRole = projectRole;
    this.allocatedChapterReferences = [];
    this.status = 'invited';
    this.weeklyHours = 38;
    this.rateAmount = 0;
    this.ratePeriod = 'week';
    this.weeklyCost = 0;
    this.invitedAt = new Date().toISOString();
  }
}

export interface TeamSprintSummary {
  sprintReference: string;
  title: string;
  completedAt: string;
  durationWeeks: number;
  completedStoryPoints: number;
  storyPointsPerWeek: number;
  weeklyTeamCost: number;
  costPerStoryPoint: number;
}

export class Team {
  reference: string;
  title: string;
  description = '';
  active = true;
  members: TeamMember[] = [];
  sprintHistory: TeamSprintSummary[] = [];
  activeSprintReference?: string;
  defaultSprintRate: number = 20;

  agentBudgetCents: number = 0;
  agentBudgetSpentCents: number = 0;
  agentBudgetPrepaidCents: number = 0;

  constructor(title: string) {
    this.reference = Guid.newGuid();
    this.title = title;
  }

  get activeMembers(): TeamMember[] {
    return this.members.filter(member => member.status === 'active');
  }

  get weeklyCost(): number {
    return this.activeMembers.reduce((total, member) => total + member.weeklyCost, 0);
  }

  get sprintRate(): number {
    return this.sprintHistory[0]?.storyPointsPerWeek || this.defaultSprintRate;
  }

  get costPerStoryPoint(): number {
    return this.sprintRate > 0 ? Math.round((this.weeklyCost / this.sprintRate) * 100) / 100 : 0;
  }

  get agentBudgetBalance(): number {
    return Math.max(0, (this.agentBudgetCents || 0) / 100);
  }

  get agentBudgetSpent(): number {
    return (this.agentBudgetSpentCents || 0) / 100;
  }

  get agentBudgetPrepaid(): number {
    return (this.agentBudgetPrepaidCents || 0) / 100;
  }
}

export function calculateWeeklyCost(member: TeamMember): number {
  const amount = Number(member.rateAmount) || 0;
  const hours = Number(member.weeklyHours) || 0;

  let cost = 0;
  switch (member.ratePeriod) {
    case 'hour':
      cost = amount * hours;
      break;
    case 'day':
      cost = amount * (hours / 7.6);
      break;
    case 'week':
    default:
      cost = amount;
      break;
  }
  return Math.round(cost * 100) / 100;
}
